/*  Greatly Social – Files.js  (v4.0 – modernized UI)
    ------------------------------------------------------------------
    • UI modernized to match dashboard theme
    • All logic, caching, drag-and-drop, uploads, deletes unchanged
*/

import React, { useRef, useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { FolderFill } from 'react-bootstrap-icons'

/* ───────────────────────── cache helpers ───────────────────────── */
const TTL_MS    = 5 * 60 * 1000
const LS_PREFIX = 'gs_files_cache/'
const now       = () => Date.now()

function loadCache (userId, path) {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_PREFIX + userId) || '{}')
    const rec = raw[path]
    if (rec && now() - rec.ts < TTL_MS) return rec.payload
  } catch {}
  return null
}

function saveCache (userId, path, payload) {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_PREFIX + userId) || '{}')
    raw[path] = { ts: now(), payload }
    localStorage.setItem(LS_PREFIX + userId, JSON.stringify(raw))
  } catch {}
}

/* ───────────────────────── helpers ───────────────────────── */
const niceName = raw => {
  let n = raw.replace(/\/$/, '').split('/').pop()
  if (n === 'render')       n = 'Edited Videos'
  if (n === 'stock')        n = 'Stock Media'
  if (n === 'email-images') n = 'Email Images'
  return n
}

async function uploadFile ({ file, userId, prefix = '', onProgress = () => {} }) {
  const { data:{ url, fields } } = await axios.get(
    '/api/generate-presigned',
    { params:{ userId, fileName:file.name, prefix, contentType:file.type }, withCredentials:true }
  )
  const fd = new FormData()
  Object.entries(fields).forEach(([k,v]) => fd.append(k,v))
  if (!('Content-Type' in fields)) fd.append('Content-Type', file.type)
  fd.append('file', file)
  await axios.post(url, fd, { onUploadProgress:onProgress })
}

const isImg = u => /\.(jpe?g|png|gif)(\?|$)/i.test(u)
const isVid = u => /\.(mp4|mov|webm|avi)(\?|$)/i.test(u)

/* ───────────────────────── component ───────────────────────── */
export default function Files () {
  const [userId,      setUserId]    = useState(null)
  const [currentPath, setCurrent]   = useState('')
  const [subfolders,  setSubs]      = useState([])
  const [s3Files,     setFiles]     = useState([])
  const [filter,      setFilter]    = useState('all')
  const [view,        setView]      = useState('grid')
  const [uploadMsg,   setUploadMsg] = useState('')
  const [uploadPct,   setUploadPct] = useState(null)
  const [showDel,     setShowDel]   = useState(false)
  const [targetKey,   setTargetKey] = useState(null)
  const [dragKey,     setDragKey]   = useState(null)
  const [dragOver,    setDragOver]  = useState(null)
  const [loading,     setLoading]   = useState(false)

  const fileInputRef = useRef(null)
  const debounceRef  = useRef(null)

  /* ─── auth/me ─── */
  useEffect(() => { (async () => {
    try {
      const d = await (await fetch('/api/auth/me', { credentials:'include' })).json()
      if (d?.user?.id) setUserId(d.user.id)
    } catch(e) { console.error('/auth/me', e) }
  })() }, [])

  /* ─── folder list ─── */
  useEffect(() => {
    if (!userId) return
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => refreshList(), 120)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, currentPath])

  async function refreshList (force = false) {
    if (!userId) return
    const cached = force ? null : loadCache(userId, currentPath)
    if (cached) {
      setSubs(cached.subfolders)
      setFiles(cached.files)
      prefetchSubfolders(cached.subfolders)
      return
    }
    setLoading(true)
    try {
      const { data } = await axios.get('/api/files', { params:{ userId, prefix:currentPath }, withCredentials:true })
      const subs  = (data.subfolders||[]).filter(p => !p.endsWith('/avatars/'))
      const files = data.files||[]
      setSubs(subs)
      setFiles(files)
      saveCache(userId, currentPath, { subfolders:subs, files })
      prefetchSubfolders(subs)
    } catch(e) { console.error('list', e) }
    finally { setLoading(false) }
  }

  async function prefetchSubfolders (subs) {
    subs.forEach(async prefix => {
      const rel = prefix.replace(`${userId}/`, '')
      if (loadCache(userId, rel)) return
      try {
        const { data } = await axios.get('/api/files', { params:{ userId, prefix }, withCredentials:true })
        saveCache(userId, rel, {
          subfolders:(data.subfolders||[]).filter(p => !p.endsWith('/avatars/')),
          files:data.files||[]
        })
      } catch {}
    })
  }

  const refresh = () => refreshList(true)

  /* ─── folder creation ─── */
  const handleCreateFolder = async () => {
    if (!userId) return
    const name = prompt('Enter new folder name:')
    if (!name) return
    await axios.post('/api/create-folder', { userId, folderPath:`${userId}/${currentPath}${name}/` }, { withCredentials:true })
    refreshList(true)
  }

  /* ─── uploads ─── */
  async function uploadFiles (files, prefix) {
    if (!userId || !files.length) return
    for (const f of files) {
      setUploadMsg(`Uploading "${f.name}"…`)
      setUploadPct(0)
      await uploadFile({
        file:f, userId, prefix,
        onProgress: ev => {
          if (!ev.total) return
          setUploadPct(Math.round(ev.loaded/ev.total*100))
        }
      })
    }
    setUploadMsg('Upload complete!')
    setUploadPct(null)
    refreshList(true)
    setTimeout(() => setUploadMsg(''), 3000)
  }

  /* ─── delete ─── */
  const openDelFolder = p => { setTargetKey(p); setShowDel(true) }
  const openDelFile   = k => { setTargetKey(k); setShowDel(true) }
  const closeDel      = () => { setShowDel(false); setTargetKey(null) }

  const confirmDelete = async () => {
    if (!targetKey) return
    await axios.delete('/api/files', { params:{ key:targetKey }, withCredentials:true })
    closeDel()
    refreshList(true)
  }

  /* ─── drag ─── */
  const onDragStartFile  = (e, k) => { setDragKey(k); e.dataTransfer.effectAllowed='move'; e.dataTransfer.setData('text/plain', k) }
  const onDragOverFolder = (e, prefix) => { e.preventDefault(); setDragOver(prefix); e.dataTransfer.dropEffect = dragKey ? 'move' : 'copy' }
  const onDragLeaveFolder = () => setDragOver(null)

  const onDropOnFolder = async (e, prefix) => {
    e.preventDefault()
    setDragOver(null)
    const movingKey = dragKey || e.dataTransfer.getData('text/plain')
    if (movingKey) {
      const fname = movingKey.split('/').pop()
      await axios.post('/api/move-file', { oldKey:movingKey, newKey:prefix+fname }, { withCredentials:true })
      setDragKey(null)
      refreshList(true)
      return
    }
    if (e.dataTransfer.files?.length) {
      await uploadFiles(Array.from(e.dataTransfer.files), prefix.replace(`${userId}/`, ''))
    }
  }

  const onDragOverPage = e => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); e.dataTransfer.dropEffect='copy' } }
  const onDropOnPage   = async e => { if (e.dataTransfer.files?.length) { e.preventDefault(); await uploadFiles(Array.from(e.dataTransfer.files), currentPath) } }

  /* ─── filtered files ─── */
  const displayedFiles = useMemo(() => (
    s3Files
      .filter(f => filter==='all' ? true : filter==='photo' ? isImg(f.url) : isVid(f.url))
      .sort((a,b) => a.key.localeCompare(b.key))
  ), [s3Files, filter])

  /* ─── breadcrumb ─── */
  const goUp = () => {
    const parts = currentPath.replace(/\/$/, '').split('/')
    parts.pop()
    setCurrent(parts.length ? parts.join('/') + '/' : '')
  }

  /* ─── shared button style ─── */
  const btnBase = (variant = 'primary') => ({
    display     : 'flex',
    alignItems  : 'center',
    gap         : 6,
    padding     : '8px 14px',
    borderRadius: 8,
    border      : variant === 'outline' ? '1.5px solid #e5e7eb' : 'none',
    background  : variant === 'primary' ? '#2563eb'
                : variant === 'success' ? '#16a34a'
                : variant === 'danger'  ? '#ef4444'
                : 'transparent',
    color       : variant === 'outline' ? '#374151' : '#ffffff',
    fontSize    : 13,
    fontWeight  : 600,
    cursor      : 'pointer',
    transition  : 'all 0.15s',
    whiteSpace  : 'nowrap',
  })

  /* ─── JSX ─── */
  return (
    <div onDragOver={onDragOverPage} onDrop={onDropOnPage}>

      {/* Page header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ fontSize:24, fontWeight:800, color:'#111827', marginBottom:4 }}>My Files</h2>
          <p style={{ fontSize:14, color:'#6b7280', margin:0 }}>
            Upload, organize, and manage your media assets.
          </p>
        </div>

        {/* Action buttons */}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button style={btnBase('outline')} onClick={refresh}>
            <i className='fas fa-sync-alt' style={{ fontSize:11 }} />
            Refresh
          </button>
          <button style={btnBase('outline')} onClick={handleCreateFolder}>
            <i className='fas fa-folder-plus' style={{ fontSize:11, color:'#f59e0b' }} />
            New Folder
          </button>
          <button style={btnBase('primary')} onClick={() => fileInputRef.current?.click()}>
            <i className='fas fa-upload' style={{ fontSize:11 }} />
            Upload Files
          </button>
          <input
            ref={fileInputRef}
            type='file'
            multiple
            accept='image/*,video/*'
            style={{ display:'none' }}
            onChange={e => { uploadFiles(Array.from(e.target.files), currentPath); e.target.value='' }}
          />
        </div>
      </div>

      {/* Upload progress */}
      {(uploadPct !== null || uploadMsg) && (
        <div style={{
          background  : '#f8fafc',
          border      : '1px solid #e5e7eb',
          borderRadius: 10,
          padding     : '12px 16px',
          marginBottom: 20,
          display     : 'flex',
          alignItems  : 'center',
          gap         : 12,
        }}>
          <i className={uploadPct !== null ? 'fas fa-cloud-upload-alt' : 'fas fa-check-circle'}
             style={{ color: uploadPct !== null ? '#2563eb' : '#16a34a', fontSize:16 }} />
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#374151', marginBottom: uploadPct !== null ? 6 : 0 }}>
              {uploadMsg}
            </div>
            {uploadPct !== null && (
              <div style={{ height:5, borderRadius:99, background:'#e5e7eb', overflow:'hidden' }}>
                <div style={{
                  height:'100%', borderRadius:99, background:'#2563eb',
                  width:`${uploadPct}%`, transition:'width 0.2s ease',
                }} />
              </div>
            )}
          </div>
          {uploadPct !== null && (
            <span style={{ fontSize:12, fontWeight:700, color:'#2563eb' }}>{uploadPct}%</span>
          )}
        </div>
      )}

      {/* Toolbar: nav + filter + view */}
      <div style={{
        display     : 'flex',
        alignItems  : 'center',
        gap         : 10,
        flexWrap    : 'wrap',
        background  : '#ffffff',
        border      : '1px solid #e5e7eb',
        borderRadius: 12,
        padding     : '10px 16px',
        marginBottom: 20,
        boxShadow   : '0 1px 3px rgba(0,0,0,0.05)',
      }}>

        {/* Back button */}
        {currentPath && (
          <button style={btnBase('outline')} onClick={goUp}>
            <i className='fas fa-arrow-left' style={{ fontSize:11 }} />
            Back
          </button>
        )}

        {/* Current path pill */}
        {currentPath && (
          <div style={{
            fontSize:12, fontWeight:600, color:'#6b7280',
            background:'#f3f4f6', borderRadius:6, padding:'4px 10px',
          }}>
            <i className='fas fa-folder' style={{ color:'#f59e0b', marginRight:5, fontSize:11 }} />
            {currentPath.replace(/\/$/, '').split('/').pop()}
          </div>
        )}

        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          {/* Filter toggle */}
          <div style={{ display:'flex', background:'#f3f4f6', borderRadius:8, padding:3 }}>
            {['all','photo','video'].map(v => (
              <button
                key={v}
                onClick={() => setFilter(v)}
                style={{
                  padding     : '5px 12px',
                  borderRadius: 6,
                  border      : 'none',
                  background  : filter===v ? '#ffffff' : 'transparent',
                  color       : filter===v ? '#111827' : '#6b7280',
                  fontSize    : 12,
                  fontWeight  : filter===v ? 700 : 500,
                  cursor      : 'pointer',
                  boxShadow   : filter===v ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition  : 'all 0.12s',
                }}
              >
                {v==='all' ? 'All' : v==='photo' ? '🖼 Photos' : '🎬 Videos'}
              </button>
            ))}
          </div>

          {/* View toggle */}
          <div style={{ display:'flex', background:'#f3f4f6', borderRadius:8, padding:3 }}>
            {[['grid','fas fa-th'], ['list','fas fa-list']].map(([v, icon]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  padding     : '6px 10px',
                  borderRadius: 6,
                  border      : 'none',
                  background  : view===v ? '#ffffff' : 'transparent',
                  color       : view===v ? '#2563eb' : '#9ca3af',
                  fontSize    : 13,
                  cursor      : 'pointer',
                  boxShadow   : view===v ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition  : 'all 0.12s',
                }}
              >
                <i className={icon} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'40px 0', gap:10, color:'#9ca3af', fontSize:14 }}>
          <div className='spinner-border spinner-border-sm' style={{ color:'#d1d5db' }} />
          Loading files…
        </div>
      )}

      {!loading && (
        <>
          {/* ── Folders ── */}
          <div style={{ marginBottom:28 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
              <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#9ca3af' }}>
                Folders
              </span>
              {subfolders.length > 0 && (
                <span style={{ fontSize:11, fontWeight:600, color:'#6b7280', background:'#f3f4f6', borderRadius:99, padding:'1px 8px' }}>
                  {subfolders.length}
                </span>
              )}
            </div>

            {subfolders.length === 0 ? (
              <p style={{ fontSize:13, color:'#9ca3af', fontStyle:'italic' }}>No subfolders here.</p>
            ) : (
              <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                {subfolders.map(prefix => (
                  <div
                    key={prefix}
                    onDragOverCapture={e => onDragOverFolder(e, prefix)}
                    onDragLeave={onDragLeaveFolder}
                    onDropCapture={e => onDropOnFolder(e, prefix)}
                    style={{
                      position    : 'relative',
                      display     : 'flex',
                      flexDirection:'column',
                      alignItems  : 'center',
                      gap         : 6,
                      padding     : '14px 10px 10px',
                      background  : dragOver === prefix ? '#eff6ff' : '#ffffff',
                      border      : `1.5px solid ${dragOver === prefix ? '#2563eb' : '#e5e7eb'}`,
                      borderRadius: 10,
                      width       : 90,
                      cursor      : 'pointer',
                      transition  : 'all 0.15s',
                      boxShadow   : '0 1px 3px rgba(0,0,0,0.05)',
                    }}
                  >
                    <div onClick={() => setCurrent(prefix.replace(`${userId}/`, ''))}>
                      <FolderFill size={34} color='#f59e0b' />
                      <div style={{ fontSize:11, fontWeight:600, color:'#374151', marginTop:5, textAlign:'center', wordBreak:'break-word', lineHeight:1.3 }}>
                        {niceName(prefix)}
                      </div>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); openDelFolder(prefix) }}
                      style={{
                        position    : 'absolute',
                        top         : 4,
                        right       : 4,
                        background  : 'transparent',
                        border      : 'none',
                        cursor      : 'pointer',
                        color       : '#9ca3af',
                        fontSize    : 16,
                        lineHeight  : 1,
                        padding     : '0 2px',
                        borderRadius: 4,
                      }}
                    >
                      ···
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Files ── */}
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
              <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#9ca3af' }}>
                Files
              </span>
              {displayedFiles.length > 0 && (
                <span style={{ fontSize:11, fontWeight:600, color:'#6b7280', background:'#f3f4f6', borderRadius:99, padding:'1px 8px' }}>
                  {displayedFiles.length}
                </span>
              )}
            </div>

            {displayedFiles.length === 0 ? (
              <div style={{
                textAlign   : 'center',
                padding     : '40px 24px',
                background  : '#ffffff',
                border      : '1px dashed #e5e7eb',
                borderRadius: 12,
                color       : '#9ca3af',
                fontSize    : 14,
              }}>
                <i className='fas fa-cloud-upload-alt' style={{ fontSize:28, marginBottom:10, display:'block' }} />
                No files here. Drag & drop or click Upload Files to add media.
              </div>
            ) : view === 'grid' ? (
              /* Grid view */
              <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                {displayedFiles.map(f => (
                  <div
                    key={f.key}
                    draggable
                    onDragStart={e => onDragStartFile(e, f.key)}
                    style={{
                      position    : 'relative',
                      width       : 100,
                      height      : 100,
                      borderRadius: 10,
                      overflow    : 'hidden',
                      border      : '1.5px solid #e5e7eb',
                      background  : '#f3f4f6',
                      boxShadow   : '0 1px 3px rgba(0,0,0,0.06)',
                      flexShrink  : 0,
                    }}
                  >
                    {isImg(f.url)
                      ? <img src={f.url} alt='' style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                      : isVid(f.url)
                        ? <video src={f.url} muted loop style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                        : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <i className='fas fa-file fa-2x' style={{ color:'#d1d5db' }} />
                          </div>
                    }
                    <button
                      onClick={() => openDelFile(f.key)}
                      style={{
                        position    : 'absolute',
                        top         : 4,
                        right       : 4,
                        background  : 'rgba(0,0,0,0.5)',
                        border      : 'none',
                        borderRadius: 6,
                        color       : '#fff',
                        fontSize    : 14,
                        lineHeight  : 1,
                        padding     : '2px 5px',
                        cursor      : 'pointer',
                      }}
                    >
                      ···
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              /* List view */
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {displayedFiles.map(f => (
                  <div
                    key={f.key}
                    draggable
                    onDragStart={e => onDragStartFile(e, f.key)}
                    style={{
                      display     : 'flex',
                      alignItems  : 'center',
                      gap         : 14,
                      background  : '#ffffff',
                      border      : '1px solid #e5e7eb',
                      borderRadius: 10,
                      padding     : '10px 14px',
                      boxShadow   : '0 1px 2px rgba(0,0,0,0.04)',
                    }}
                  >
                    <div style={{ width:52, height:52, borderRadius:8, overflow:'hidden', border:'1px solid #e5e7eb', flexShrink:0 }}>
                      {isImg(f.url)
                        ? <img src={f.url} alt='' style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                        : isVid(f.url)
                          ? <video src={f.url} muted loop style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                          : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:'#f3f4f6' }}>
                              <i className='fas fa-file' style={{ color:'#d1d5db', fontSize:18 }} />
                            </div>
                      }
                    </div>
                    <div style={{ flex:1, overflow:'hidden' }}>
                      <div style={{ fontSize:13, fontWeight:600, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {f.key.split('/').pop()}
                      </div>
                      <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>
                        {isImg(f.url) ? 'Image' : isVid(f.url) ? 'Video' : 'File'}
                      </div>
                    </div>
                    <button
                      onClick={() => openDelFile(f.key)}
                      style={{
                        padding     : '6px 12px',
                        background  : 'transparent',
                        border      : '1.5px solid #e5e7eb',
                        borderRadius: 7,
                        color       : '#9ca3af',
                        fontSize    : 12,
                        fontWeight  : 600,
                        cursor      : 'pointer',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Delete confirmation modal */}
      {showDel && (
        <>
          <div
            onClick={closeDel}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1050, backdropFilter:'blur(2px)' }}
          />
          <div style={{
            position    : 'fixed',
            top         : '50%',
            left        : '50%',
            transform   : 'translate(-50%, -50%)',
            zIndex      : 1051,
            background  : '#ffffff',
            borderRadius: 14,
            width       : 380,
            maxWidth    : 'calc(100vw - 32px)',
            boxShadow   : '0 20px 60px rgba(0,0,0,0.18)',
            overflow    : 'hidden',
          }}>
            <div style={{ padding:'20px 22px 16px', borderBottom:'1px solid #f3f4f6', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:32, height:32, borderRadius:'50%', background:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <i className='fas fa-trash' style={{ fontSize:12, color:'#ef4444' }} />
              </div>
              <h3 style={{ fontSize:16, fontWeight:800, color:'#111827', margin:0 }}>Delete Item</h3>
              <button onClick={closeDel} style={{ marginLeft:'auto', background:'transparent', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:20 }}>×</button>
            </div>
            <div style={{ padding:'16px 22px' }}>
              <p style={{ fontSize:13, color:'#374151', lineHeight:1.6, margin:0 }}>
                Are you sure you want to delete this item? Any scheduled post using it may be affected.
              </p>
            </div>
            <div style={{ padding:'12px 22px 18px', borderTop:'1px solid #f3f4f6', display:'flex', justifyContent:'flex-end', gap:8 }}>
              <button onClick={closeDel} style={{ padding:'8px 16px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'transparent', color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={confirmDelete} style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#ef4444', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                Yes, Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}